import { supabase } from '../lib/supabase';
import { useToast } from './useToast';
import { useConfirm } from './useConfirm';
import type { Project, DiversSubProject } from '../types';
import {
  SIEGE_COORDS,
  calculateDistance,
  determineZone,
  type SubProjectFormData,
  type DiversFormData,
} from './useProjectsData';

interface UseProjectsActionsParams {
  // data
  projects: Project[];
  subProjects: DiversSubProject[];
  loadProjects: () => Promise<void>;
  setLoading: (v: boolean) => void;
  // form édition
  editingId: string | null;
  setEditingId: (v: string | null) => void;
  formData: Partial<Project>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<Project>>>;
  setIsGeocoding: (v: boolean) => void;
  // form divers
  diversFormData: DiversFormData;
  setDiversFormData: React.Dispatch<React.SetStateAction<DiversFormData>>;
  setShowDiversForm: (v: boolean) => void;
  // form sub-project
  subProjectFormData: SubProjectFormData;
  setSubProjectFormData: React.Dispatch<React.SetStateAction<SubProjectFormData>>;
  setShowSubProjectModal: (v: boolean) => void;
  setIsGeocodingSubProject: (v: boolean) => void;
  setIsSavingSubProject: (v: boolean) => void;
}

export function useProjectsActions(params: UseProjectsActionsParams) {
  const { toast } = useToast();
  const { confirm: confirmDialog } = useConfirm();

  const {
    projects,
    subProjects,
    loadProjects,
    setLoading,
    editingId,
    setEditingId,
    formData,
    setFormData,
    setIsGeocoding,
    diversFormData,
    setDiversFormData,
    setShowDiversForm,
    subProjectFormData,
    setSubProjectFormData,
    setShowSubProjectModal,
    setIsGeocodingSubProject,
    setIsSavingSubProject,
  } = params;

  // Recherche d'adresse via API Gouv
  const handleAddressSearch = async () => {
    if (!formData.address || formData.address.length < 3) return;

    setIsGeocoding(true);
    try {
      const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(formData.address)}&limit=1`);
      if (!response.ok) throw new Error("Erreur réseau API Adresse");

      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        const [lng, lat] = feature.geometry.coordinates;
        const label = feature.properties.label;

        const distance = calculateDistance(SIEGE_COORDS.lat, SIEGE_COORDS.lng, lat, lng);
        const zone = determineZone(distance);

        setFormData(prev => ({
          ...prev,
          address: label,
          lat: lat,
          lng: lng,
          zone: zone
        }));
      } else {
        toast.warning("Adresse introuvable. Veuillez vérifier la saisie.");
      }
    } catch (error) {
      console.error("Erreur géocodage:", error);
      toast.error("Erreur technique lors de la recherche d'adresse.");
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleResetForm = () => {
    setEditingId(null);
    setFormData({
      code: '', name: '',
      estimated_hours: 0,
      estimated_hours_be: 0,
      is_active: true,
      address: '', lat: undefined, lng: undefined, zone: undefined,
      gd_amount: undefined, gd_travel_hours: undefined,
      devis_amount: undefined
    });
  };

  const handleEdit = (p: Project) => {
    setEditingId(p.id);
    setFormData({
      code: p.code,
      name: p.name,
      estimated_hours: p.estimated_hours,
      estimated_hours_be: p.estimated_hours_be || 0,
      is_active: p.is_active,
      address: p.address || '',
      devis_amount: p.devis_amount ?? undefined,
      lat: p.lat,
      lng: p.lng,
      zone: p.zone,
      gd_amount: p.gd_amount,
      gd_travel_hours: p.gd_travel_hours
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.code || !formData.name) {
        throw new Error("Les champs Code et Nom sont obligatoires.");
      }

      const payload = { ...formData };

      if (editingId) {
        const { error } = await supabase.from('projects').update(payload).eq('id', editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('projects').insert(payload);
        if (error) {
          if (error.code === '23505') throw new Error("Ce code projet existe déjà.");
          throw error;
        }
      }

      handleResetForm();
      await loadProjects();

    } catch (error) {
      const msg = error instanceof Error ? error.message : "Erreur inconnue";
      if (msg.includes('invalid input syntax')) {
        toast.error("Erreur base de données : la colonne 'zone' doit être de type texte. Veuillez exécuter le script SQL fourni.");
      } else {
        toast.error("Erreur lors de la sauvegarde : " + msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleArchive = async (p: Project) => {
    const ok = await confirmDialog({
      title: p.is_active ? "Archiver ce projet ?" : "Réactiver ce projet ?",
      message: p.is_active ? "Il ne sera plus visible pour la saisie." : "Le projet sera de nouveau accessible.",
      variant: p.is_active ? "danger" : "info"
    });
    if (!ok) return;

    setLoading(true);
    try {
      const { error } = await supabase.from('projects').update({ is_active: !p.is_active }).eq('id', p.id);
      if (error) throw error;
      await loadProjects();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la modification du statut.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (p: Project) => {
    const ok = await confirmDialog({
      title: "Supprimer ce chantier ?",
      message: `Supprimer définitivement "${p.name}" (${p.code}) ? Cette action est irréversible et supprimera toutes les données associées.`,
      variant: "danger"
    });
    if (!ok) return;

    setLoading(true);
    try {
      const { error } = await supabase.from('projects').delete().eq('id', p.id);
      if (error) throw error;
      await loadProjects();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression. Le projet est peut-être lié à des données existantes.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubProject = async (subProject: DiversSubProject) => {
    const ok = await confirmDialog({
      title: "Supprimer ce sous-chantier ?",
      message: `Supprimer définitivement "${subProject.divers_number} - ${subProject.name}" ? Cette action est irréversible et supprimera toutes les données associées.`,
      variant: "danger"
    });
    if (!ok) return;

    setLoading(true);
    try {
      const { error } = await supabase.from('divers_sub_projects').delete().eq('id', subProject.id);
      if (error) throw error;
      await loadProjects();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression. Le sous-chantier est peut-être lié à des données existantes.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSubProjectArchive = async (subProject: DiversSubProject) => {
    const isArchiving = subProject.is_active;
    const action = isArchiving ? 'Archiver' : 'Réactiver';

    const ok = await confirmDialog({
      title: `${action} ce sous-chantier ?`,
      message: `${action} "${subProject.divers_number} - ${subProject.name}" ?${isArchiving ? ' Il ne sera plus visible dans la liste active mais les données seront conservées.' : ''}`,
      variant: isArchiving ? "danger" : "info"
    });
    if (!ok) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('divers_sub_projects')
        .update({ is_active: !subProject.is_active })
        .eq('id', subProject.id);

      if (error) throw error;
      await loadProjects();
    } catch (error) {
      console.error(error);
      toast.error(`Erreur lors de ${isArchiving ? "l'archivage" : "la réactivation"} du sous-chantier.`);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveDiversGroup = async (parentProject: Project) => {
    const activeSubProjects = subProjects.filter(sp => sp.parent_project_id === parentProject.id && sp.is_active);

    const ok = await confirmDialog({
      title: "Archiver ce groupe ?",
      message: `Archiver entièrement le groupe "${parentProject.name}" ? Le groupe et ses ${activeSubProjects.length} sous-chantier(s) actif(s) seront archivés. Cette action conserve les données mais les masque de la liste active.`,
      variant: "danger"
    });
    if (!ok) return;

    setLoading(true);
    try {
      if (activeSubProjects.length > 0) {
        const { error: subError } = await supabase
          .from('divers_sub_projects')
          .update({ is_active: false })
          .eq('parent_project_id', parentProject.id)
          .eq('is_active', true);
        if (subError) throw subError;
      }

      const { error: parentError } = await supabase
        .from('projects')
        .update({ is_active: false })
        .eq('id', parentProject.id);
      if (parentError) throw parentError;

      await loadProjects();
      toast.success(`Groupe "${parentProject.name}" archivé avec ${activeSubProjects.length} sous-chantier(s).`);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'archivage du groupe Divers.");
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateDiversGroup = async (parentProject: Project) => {
    const archivedSubs = subProjects.filter(sp => sp.parent_project_id === parentProject.id && !sp.is_active);

    const ok = await confirmDialog({
      title: "Réactiver ce groupe ?",
      message: `Réactiver le groupe "${parentProject.name}" ? Le groupe et ses ${archivedSubs.length} sous-chantier(s) archivé(s) seront réactivés.`,
      variant: "info"
    });
    if (!ok) return;

    setLoading(true);
    try {
      if (archivedSubs.length > 0) {
        const { error: subError } = await supabase
          .from('divers_sub_projects')
          .update({ is_active: true })
          .eq('parent_project_id', parentProject.id)
          .eq('is_active', false);
        if (subError) throw subError;
      }

      const { error: parentError } = await supabase
        .from('projects')
        .update({ is_active: true })
        .eq('id', parentProject.id);
      if (parentError) throw parentError;

      await loadProjects();
      toast.success(`Groupe "${parentProject.name}" réactivé avec ${archivedSubs.length} sous-chantier(s).`);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la réactivation du groupe Divers.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDiversGroup = async (diversProject: Project) => {
    const linkedSubProjects = subProjects.filter(sp => sp.parent_project_id === diversProject.id);
    if (linkedSubProjects.length > 0) {
      toast.warning(`Impossible de supprimer ce groupe. Il contient encore ${linkedSubProjects.length} sous-chantier(s). Veuillez d'abord archiver ou supprimer tous les sous-chantiers.`);
      return;
    }

    const { data: assignments, error: checkError } = await supabase
      .from('project_assignments')
      .select('id')
      .eq('project_id', diversProject.id)
      .limit(1);

    if (checkError) {
      console.error(checkError);
      toast.error("Erreur lors de la vérification des assignations.");
      return;
    }

    if (assignments && assignments.length > 0) {
      toast.warning("Impossible de supprimer ce groupe. Des assignations existent pour ce projet. Veuillez d'abord supprimer les assignations liées.");
      return;
    }

    const ok = await confirmDialog({
      title: "Supprimer ce groupe ?",
      message: `Supprimer définitivement le groupe "${diversProject.name}" ? Cette action est irréversible.`,
      variant: "danger"
    });
    if (!ok) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', diversProject.id);

      if (error) throw error;
      await loadProjects();
      toast.success(`Groupe "${diversProject.name}" supprimé avec succès.`);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression du groupe Divers.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDiversGroup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!diversFormData.code || !diversFormData.name || !diversFormData.parent_entity) {
      toast.warning("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        code: diversFormData.code,
        name: diversFormData.name,
        parent_entity: diversFormData.parent_entity,
        category: 'divers' as const,
        is_active: true,
        estimated_hours: 0,
        estimated_hours_be: 0
      };

      const { error } = await supabase.from('projects').insert(payload);

      if (error) {
        if (error.code === '23505') throw new Error("Ce code projet existe déjà.");
        throw error;
      }

      setDiversFormData({ code: '', name: '', parent_entity: '' });
      setShowDiversForm(false);
      await loadProjects();
      toast.success(`Groupe Divers "${diversFormData.name}" créé avec succès`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Erreur inconnue";
      toast.error("Erreur lors de la création : " + msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubProjectAddressSearch = async () => {
    if (!subProjectFormData.address || subProjectFormData.address.length < 3) return;

    setIsGeocodingSubProject(true);
    try {
      const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(subProjectFormData.address)}&limit=1`);
      if (!response.ok) throw new Error("Erreur réseau API Adresse");

      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        const [lng, lat] = feature.geometry.coordinates;
        const label = feature.properties.label;

        const distance = calculateDistance(SIEGE_COORDS.lat, SIEGE_COORDS.lng, lat, lng);
        const zone = determineZone(distance);

        setSubProjectFormData(prev => ({
          ...prev,
          address: label,
          lat: lat,
          lng: lng,
          zone: zone
        }));
      } else {
        toast.warning("Adresse introuvable. Veuillez vérifier la saisie.");
      }
    } catch (error) {
      console.error("Erreur géocodage sous-chantier:", error);
      toast.error("Erreur technique lors de la recherche d'adresse.");
    } finally {
      setIsGeocodingSubProject(false);
    }
  };

  const handleCreateSubProject = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subProjectFormData.parent_project_id || !subProjectFormData.divers_number || !subProjectFormData.name) {
      toast.warning("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    setIsSavingSubProject(true);
    try {
      const payload = {
        parent_project_id: subProjectFormData.parent_project_id,
        divers_number: subProjectFormData.divers_number.trim(),
        name: subProjectFormData.name.trim(),
        address: subProjectFormData.address || null,
        lat: subProjectFormData.lat || null,
        lng: subProjectFormData.lng || null,
        zone: subProjectFormData.zone || null,
        is_active: true
      };

      const { error } = await supabase.from('divers_sub_projects').insert(payload);

      if (error) {
        if (error.code === '23505') throw new Error("Un chantier avec ce nom existe déjà dans ce groupe.");
        throw error;
      }

      setSubProjectFormData({
        divers_number: '',
        name: '',
        address: '',
        lat: undefined,
        lng: undefined,
        zone: undefined,
        parent_project_id: ''
      });
      setShowSubProjectModal(false);
      await loadProjects();
      toast.success(`Sous-chantier "${subProjectFormData.divers_number} - ${subProjectFormData.name}" créé avec succès`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Erreur inconnue";
      toast.error("Erreur lors de la création : " + msg);
    } finally {
      setIsSavingSubProject(false);
    }
  };

  // Suppress unused warning for projects param (kept for parity / future use)
  void projects;

  return {
    handleAddressSearch,
    handleResetForm,
    handleEdit,
    handleSave,
    toggleArchive,
    handleDelete,
    handleDeleteSubProject,
    handleToggleSubProjectArchive,
    handleArchiveDiversGroup,
    handleReactivateDiversGroup,
    handleDeleteDiversGroup,
    handleCreateDiversGroup,
    handleSubProjectAddressSearch,
    handleCreateSubProject,
  };
}
